/**
 * @file captureApi.ts
 * @brief Upload + create boardroom session from a browser Blob.
 *
 * @author DES
 * @date 28-07-2026
 */

import { z } from "zod";
import { apiPost } from "@/shared/api/client";
import { boardroomEndpoints } from "@/shared/api/endpoints";
import { MeetingDetailResponseSchema } from "@/features/company-meetings/schemas";
import {
  draftAgendaIds,
  type BoardroomDraftMeta,
} from "./capture/boardroomDraftStore";
import { agendaChecksForUpload } from "./agendaChecks";

const RequestUploadSchema = z.object({
  success: z.literal(true),
  meetingId: z.string(),
  uploadUrl: z.string().url(),
  publicUrl: z.string(),
  requiredHeaders: z.record(z.string(), z.string()).optional(),
});

export type CreateFromBlobInput = {
  blob: Blob;
  meta: BoardroomDraftMeta;
  fileName?: string;
};

export type BoardroomUploadPhase =
  | "authorizing"
  | "uploading"
  | "creating";

export type BoardroomUploadProgress = {
  phase: BoardroomUploadPhase;
  /** Overall 0..100 across authorize → upload → create. */
  percent: number;
};

const UPLOAD_RANGE_START = 15;
const UPLOAD_RANGE_END = 88;

function putBlobWithProgress(
  url: string,
  blob: Blob,
  headers: Record<string, string>,
  onUploadRatio: (ratio: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }
    xhr.upload.onprogress = (ev) => {
      if (!ev.lengthComputable || ev.total <= 0) return;
      onUploadRatio(Math.min(1, ev.loaded / ev.total));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onUploadRatio(1);
        resolve();
        return;
      }
      reject(
        new Error(
          `Cloud upload failed (${xhr.status}). Keep the tab open and retry.`,
        ),
      );
    };
    xhr.onerror = () => {
      reject(
        new Error(
          "Cloud upload failed (network). Keep the tab open and retry.",
        ),
      );
    };
    xhr.send(blob);
  });
}

export async function uploadAndCreateBoardroomMeeting(
  input: CreateFromBlobInput,
  onProgress?: (progress: BoardroomUploadProgress) => void,
) {
  const report = (phase: BoardroomUploadPhase, percent: number) => {
    onProgress?.({
      phase,
      percent: Math.max(0, Math.min(100, Math.round(percent))),
    });
  };

  const contentType =
    input.meta.contentType || input.blob.type || "audio/webm";
  const fileName =
    input.fileName ||
    `boardroom_${Date.now()}.${input.meta.extension || "webm"}`;

  report("authorizing", 8);
  const signedRaw = await apiPost<unknown>(boardroomEndpoints.requestUpload, {
    fileName,
    contentType,
  });
  const signed = RequestUploadSchema.parse(signedRaw);
  report("authorizing", UPLOAD_RANGE_START);

  const headers: Record<string, string> = {
    "Content-Type": contentType,
    ...(signed.requiredHeaders ?? {}),
  };

  report("uploading", UPLOAD_RANGE_START);
  await putBlobWithProgress(signed.uploadUrl, input.blob, headers, (ratio) => {
    report(
      "uploading",
      UPLOAD_RANGE_START +
        ratio * (UPLOAD_RANGE_END - UPLOAD_RANGE_START),
    );
  });
  report("uploading", UPLOAD_RANGE_END);

  const durationSec = Math.max(
    1,
    Math.round((input.meta.elapsedMs || 0) / 1000),
  );

  report("creating", 92);
  const createRaw = await apiPost<unknown>(boardroomEndpoints.create, {
    meetingId: signed.meetingId,
    title: input.meta.title,
    recordingUrl: signed.publicUrl,
    recordingSizeBytes: input.blob.size,
    totalDurationSec: durationSec,
    targetLanguages: input.meta.targetLanguages,
    participants: input.meta.participants,
    bookmarks: input.meta.bookmarks.map((b) => ({
      id: b.id,
      type: b.type,
      timestamp: b.timestamp,
      timeInSecs: b.timeInSecs,
    })),
    paymentIntentId: input.meta.paymentIntentId ?? undefined,
    authorizedCents: input.meta.authorizedCents ?? undefined,
    scheduledMeetingId: input.meta.scheduledMeetingId ?? undefined,
    recordingNotice: input.meta.recordingNotice ?? undefined,
    // B49: the agenda check boxes, every shown item; left out when none were
    // shown (no agenda, Meeting Notes, switch off), as an older app does.
    ...(input.meta.tier !== "notes" && Array.isArray(input.meta.agendaChecks)
      ? { agendaChecks: agendaChecksForUpload(input.meta.agendaChecks) }
      : {}),
    // Meeting Notes (ADR-030, 06-10-2026): sent only for Notes, so a full
    // meeting's body is exactly what it was before tiers existed.
    ...(input.meta.tier === "notes" ? { tier: "notes" } : {}),
  });
  report("creating", 100);

  const meeting = MeetingDetailResponseSchema.parse(createRaw).meeting;

  // Attach the agenda chosen at setup, now that there is a meeting to attach
  // it to (ADR-023). Deliberately fire-and-forget: a failed LINK must never
  // fail a recording that is already safely in GCS and the database — the note
  // can be attached afterwards from either side. Same rule as the mobile
  // recorder. (21-08-2026)
  // Every agenda note, one link each (30-09-2026, mobile parity). An older
  // draft's single `prepNoteId` is read too (draftAgendaIds).
  for (const noteId of draftAgendaIds(input.meta)) {
    void apiPost(boardroomEndpoints.prepNotes(meeting.id), { noteId }).catch(
      (err) => {
        console.warn("[captureApi] Prep note link failed:", noteId, err);
      },
    );
  }

  return meeting;
}

export type ResolveSpeakerInput = {
  speakerId: string;
  name: string;
  isExternal: boolean;
  profileImageUrl?: string | null;
  email?: string;
  /** The account behind the name, when known (B37 contract). */
  userId?: string;
};

export async function resolveBoardroomSpeakers(
  meetingId: string,
  resolvedSpeakers: ResolveSpeakerInput[],
) {
  const raw = await apiPost<unknown>(boardroomEndpoints.resolve(meetingId), {
    resolvedSpeakers,
  });
  return MeetingDetailResponseSchema.parse(raw).meeting;
}

export async function autoSynthesizeBoardroom(meetingId: string) {
  const raw = await apiPost<unknown>(
    boardroomEndpoints.autoSynthesize(meetingId),
    {},
  );
  return MeetingDetailResponseSchema.parse(raw).meeting;
}
